/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { sizeToCSS } from '../utils/UNSAFE_size';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useEffect, useRef } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import {
  usePointerEvents,
  useKeyboardEvents,
  useMeterDatatip
} from '../hooks/PRIVATE_useDvtMeterEvents';

import {
  getMeterAriaProps,
  validateRange,
  getTrackAndIndicatorColor
} from '../utils/PRIVATE_meterUtils';

import { Threshold } from '../utils/UNSAFE_meterTypes';
import { BarTrack, BarValue } from '../PRIVATE_Meter';
import { getDimensions, getRefLinesInfo, getValue } from './MeterUtils';
import { ReferenceLineBar } from './ReferenceLines';
import { ThresholdBar } from './ThresholdBar';
import { MeterBarProps } from './MeterBar.types';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
/**
 * A Meter bar displays information graphically in a rectangular bar, highlighting a specific metric value's progress in relation to its min, max, or thresholds.
 */
export function MeterBar({
  max = 100,
  min = 0,
  value = 0,
  step = 1,
  length = '100%',
  size = 'lg',
  orientation = 'horizontal',
  isTrackRendered = true,
  thresholdDisplay = 'all',
  indicatorSize = 1,
  ...props
}: MeterBarProps) {
  validateRange(min, max, value, step);

  const isHoriz = orientation === 'horizontal';
  const barTrackRef = useRef<HTMLDivElement>(null);
  const dimensionsRef = useRef(getDimensions(null));
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  useEffect(() => {
    if (props.onCommit || props.onInput) {
      dimensionsRef.current = getDimensions(barTrackRef.current);
    }
  }, [size, length, orientation, props.onCommit, props.onInput]);

  const getValueFromEvent = (event: PointerEvent): number | undefined => {
    // only evaluate value from bar track
    if (event.target == barTrackRef.current) {
      return getValue(event, dimensionsRef, min, max, step, orientation, isRtl);
    }
    return;
  };

  const pointerEventsProps = usePointerEvents(
    value,
    getValueFromEvent,
    barTrackRef,
    props.onCommit,
    props.onInput
  );

  const keyboardEventsProps = useKeyboardEvents(
    value,
    min,
    max,
    step,
    props.onCommit,
    props.onInput
  );

  const { datatipContent, datatipProps } = useMeterDatatip(
    value,
    props.datatip,
    props.ariaDescribedBy
  );

  const ariaLabelProps = getMeterAriaProps(
    value,
    min,
    max,
    `${value}`,
    props.accessibleLabel,
    props.ariaLabelledBy,
    props.thresholds
  );
  const mergedProps = mergeProps(
    pointerEventsProps,
    keyboardEventsProps,
    datatipProps,
    ariaLabelProps
  );

  const validIndicatorSize = Math.max(0, Math.min(1, indicatorSize));
  const valueBarLength = `${((value - min) * 100) / (max - min)}%`;
  const valueBarSize = `${validIndicatorSize * 100}%`;
  const { trackColor, indicatorColor } = getTrackAndIndicatorColor(
    value,
    thresholdDisplay,
    props.trackColor,
    props.indicatorColor,
    props.thresholds
  );

  const sizeStyle = getRefLinesInfo(props.referenceLines);
  const isInteractive = props.onCommit || props.onInput;
  const { isTabbable } = useTabbableMode();
  return (
    <>
      <div
        tabIndex={!isTabbable ? -1 : 0}
        role="slider"
        {...mergedProps}
        style={{
          width: isHoriz ? sizeToCSS(length) : undefined,
          height: isHoriz ? undefined : sizeToCSS(length)
        }}
        class={classNames([componentStyles.base, componentStyles[orientation]])}>
        <BarTrack
          ref={barTrackRef}
          orientation={orientation}
          color={
            !isTrackRendered || (thresholdDisplay === 'all' && props.thresholds)
              ? 'transparent'
              : trackColor
          }
          class={classNames([
            barTrackStyles.base,
            isInteractive ? componentStyles.interactive : '',
            isHoriz ? '' : barTrackStyles.vertical,
            isHoriz ? barTrackStyles[`${size}Horizontal`] : barTrackStyles[`${size}Vertical`],
            ...getMarginClass(sizeStyle, size, isHoriz)
          ])}>
          {thresholdDisplay === 'all' &&
            props.thresholds
              ?.filter((threshold: Threshold) => threshold.max > min && threshold.max <= max)
              .sort((a, b) => a.max - b.max)
              .map((threshold: Threshold, index, thresholds) => {
                const diff =
                  index === 0 ? threshold.max - min : threshold.max - thresholds[index - 1].max;
                return (
                  <>
                    <ThresholdBar
                      class={index === 0 ? trackSectionStyle[`${orientation}First`] : ''}
                      orientation={orientation}
                      length={`${(diff * 100) / (max - min)}%`}
                      color={threshold.color}
                    />

                    {isTrackRendered && index === thresholds.length - 1 && threshold.max < max ? (
                      <ThresholdBar
                        class={trackSectionStyle[`${orientation}Last`]}
                        color={trackColor}
                        orientation={orientation}
                        length={`${((max - thresholds[index].max) * 100) / (max - min)}%`}
                      />
                    ) : (
                      ''
                    )}
                  </>
                );
              })}
          <BarValue
            length={valueBarLength}
            size={valueBarSize}
            orientation={orientation}
            class={classNames([barValueStyles.base, barValueStyles[orientation]])}
            color={indicatorColor}
          />
          {props.referenceLines?.map((item) => (
            <ReferenceLineBar
              color={item.color}
              orientation={orientation}
              percentage={`${((item.value - min) * 100) / (max - min)}%`}
              position={item.position}
            />
          ))}
        </BarTrack>
      </div>
      {datatipContent}
    </>
  );
}

const getMarginClass = function (
  info: { start: boolean; end: boolean },
  size: 'sm' | 'md' | 'lg',
  isHoriz: boolean
) {
  const className = [];
  if (info.start) {
    className.push(barTrackStyles[`${size}Margin${isHoriz ? 'Top' : 'Start'}`]);
  }

  if (info.end) {
    className.push(barTrackStyles[`${size}Margin${isHoriz ? 'Bottom' : 'End'}`]);
  }
  return className;
};

const componentStyles = {
  base: cssProps`${{
    display: 'inline-flex',
    pointerEvents: 'none',
    '&:focus-visible': {
      outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)',
      outlineStyle: 'dotted',
      outlineWidth: '1px'
    }
  }}`,
  horizontal: cssProps`${{
    width: '100%'
  }}`,
  vertical: cssProps`${{
    height: '100%'
  }}`,
  interactive: cssProps`${{
    cursor: 'pointer',
    touchAction: 'none'
  }}`
};

const barValueStyles = {
  base: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-value-bg-color)',
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm)',
    position: 'absolute',
    pointerEvents: 'none'
  }}`,
  vertical: cssProps`${{
    bottom: '0%',
    left: '50%',
    transform: 'translate(-50%, 0)'
  }}`,
  horizontal: cssProps`${{
    top: '50%',
    transform: 'translate(0, -50%)'
  }}`
};

const barTrackStyles = {
  base: cssProps`${{
    position: 'relative',
    display: 'flex',
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-track-bg-color)',
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm)',
    pointerEvents: 'auto'
  }}`,
  vertical: cssProps`${{
    flexDirection: 'column-reverse'
  }}`,
  smHorizontal: cssProps`${{
    width: '100%',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-sm-size)'
  }}`,
  smVertical: cssProps`${{
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-sm-size)',
    height: '100%'
  }}`,
  mdHorizontal: cssProps`${{
    width: '100%',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-md-size)'
  }}`,
  mdVertical: cssProps`${{
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-md-size)',
    height: '100%'
  }}`,
  lgHorizontal: cssProps`${{
    width: '100%',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-lg-size)'
  }}`,
  lgVertical: cssProps`${{
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-lg-size)',
    height: '100%'
  }}`,
  smMarginTop: cssProps`${{
    marginTop:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x)- var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-sm-size))'
  }}`,
  mdMarginTop: cssProps`${{
    marginTop:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-md-size))'
  }}`,
  lgMarginTop: cssProps`${{
    marginTop:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-lg-size))'
  }}`,
  smMarginStart: cssProps`${{
    marginInlineStart:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-sm-size))'
  }}`,
  mdMarginStart: cssProps`${{
    marginInlineStart:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-md-size))'
  }}`,
  lgMarginStart: cssProps`${{
    marginInlineStart:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-lg-size))'
  }}`,
  smMarginEnd: cssProps`${{
    marginInlineEnd:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-sm-size))'
  }}`,
  mdMarginEnd: cssProps`${{
    marginInlineEnd:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-md-size))'
  }}`,
  lgMarginEnd: cssProps`${{
    marginInlineEnd:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-lg-size))'
  }}`,
  smMarginBottom: cssProps`${{
    marginBottom:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-sm-size))'
  }}`,
  mdMarginBottom: cssProps`${{
    marginBottom:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-md-size))'
  }}`,
  lgMarginBottom: cssProps`${{
    marginBottom:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x) - var(--oj-c-PRIVATE-DO-NOT-USE-meter-bar-lg-size))'
  }}`
};

const trackSectionStyle = {
  horizontalFirst: cssProps`${{
    borderRadius:
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm) 0 0 var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm)'
  }}`,
  horizontalLast: cssProps`${{
    borderRadius:
      '0 var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm) var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm) 0',
    borderWidth: '0'
  }}`,
  verticalFirst: cssProps`${{
    borderRadius:
      '0 0 var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm) var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm)'
  }}`,
  verticalLast: cssProps`${{
    borderRadius:
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm) var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-sm) 0 0',
    borderWidth: '0'
  }}`
};
